import React, { useEffect, useRef, useState } from 'react';
import { Center, Text } from '@chakra-ui/react';
import {
  useAssignmentReview,
  useAuth,
  useCheckAssignmentAccess,
  useFixedHeaderOffset,
  useParams,
  useRedirectToHome
} from '../../hooks';
import { useGetStudentAssignment } from '../../queries';
import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import ReviewQuestionAnswer from './ReviewQuestionAnswer';
import { FullPageLoader } from '../shared';
import { AssignmentReviewDrawer } from './AssignmentReviewDrawer';
import TestHeader from '../assignments/TestHeader';
import FloatingNavigationButton from '../assignments/FloatingNavigationButton';
import ReviewBottomNavigation from './ReviewBottomNavigation';
import { getAssignmentEndUrl, getHomePageUrl, getRcContext, preloadMediaBlob, shouldRevealSolutions } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '../ui/status-badge';
import { QUESTION_TYPES, reviewStatusMap } from '../../constants';
import { buildDrawerQuestionNumbers } from '../../utils/drawerRows';

const reviewVariantMap = {
  CORRECT: 'correct',
  PARTIAL: 'partial',
  SKIPPED: 'skipped',
  INCORRECT: 'incorrect'
};

const AssignmentReview = () => {
  const { studentAssignmentId, questionId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const goHome = () => navigate(getHomePageUrl());
  const [isNavigationOpen, setIsNavigationOpen] = useState(false);
  const scrollableRef = useRef(null);
  const testHeaderRef = useRef(null);
  const scrollPaddingTop = useFixedHeaderOffset({
    headerRef: testHeaderRef,
    scrollRef: scrollableRef
  });

  const { isFetching, data: assignmentData } = useGetStudentAssignment(studentAssignmentId);
  const {
    name,
    drawerData,
    isFirstQuestion,
    isLastQuestion,
    currentQuestion,
    currentQuestionIndex,
    currentQuestionResponse,
    currentAnswerScoreData,
    onNext,
    onPrevious,
    answerStatus,
    totalQuestions,
    isRedirecting
  } = useAssignmentReview({
    assignmentData
  });
  const { redirectToHome } = useRedirectToHome();

  useCheckAssignmentAccess({ assignmentData });

  useEffect(() => {
    if (!assignmentData || isFetching) return;
    if (!shouldRevealSolutions(assignmentData)) {
      navigate(getAssignmentEndUrl({ studentAssignmentId }), { replace: true });
    }
  }, [assignmentData, isFetching, navigate, studentAssignmentId]);

  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      scrollableRef.current?.scrollTo({ top: 0, behavior: 'instant' });
    };

    resetScroll();
    const timeoutId = setTimeout(resetScroll, 50);
    return () => clearTimeout(timeoutId);
  }, [questionId]);

  useEffect(() => {
    if (!token || !assignmentData?.studentResponse?.length) return;

    const questionTypeById = new Map(
      (assignmentData.assignment?.questions || []).map((q) => [q.id, q.type])
    );
    const responses = assignmentData.studentResponse;
    const indices = [currentQuestionIndex - 1, currentQuestionIndex, currentQuestionIndex + 1];

    indices.forEach((index) => {
      const response = responses[index];
      if (!response?.uploadedFile?.fileKey) return;

      const type = questionTypeById.get(response.questionId);
      if (type !== QUESTION_TYPES.AUDIO && type !== QUESTION_TYPES.VIDEO) return;

      preloadMediaBlob({
        fileKey: response.uploadedFile.fileKey,
        token,
        questionId: response.questionId
      });
    });
  }, [token, assignmentData, currentQuestionIndex]);

  // The student's numbers - 2b, 3.2a - in the order their paper was dealt, so the header calls
  // a question what the tray calls it.
  const questionNumbers = React.useMemo(
    () =>
      buildDrawerQuestionNumbers({
        sections: assignmentData?.assignment?.sections || [],
        assignmentQuestions: assignmentData?.assignment?.questions || [],
        responses: assignmentData?.studentResponse
      }),
    [
      assignmentData?.assignment?.sections,
      assignmentData?.assignment?.questions,
      assignmentData?.studentResponse
    ]
  );
  const questionLabel = questionNumbers[String(questionId)] || '';

  if (isFetching || isRedirecting || (assignmentData && !shouldRevealSolutions(assignmentData))) {
    return <FullPageLoader />;
  }

  if (!assignmentData && !isFetching) {
    redirectToHome();
    return (
      <Center w="100%" h="100%">
        <Text>Invalid Assignment ID</Text>
      </Center>
    );
  }

  const rcContext = getRcContext(questionId, assignmentData);
  const statusInfo = answerStatus ? reviewStatusMap[answerStatus] : null;
  const statusBadge = statusInfo ? (
    <StatusBadge variant={reviewVariantMap[answerStatus] ?? 'default'}>
      {statusInfo.uiText}
    </StatusBadge>
  ) : null;

  return (
    <LoggedInUserWrapper
      testMode
      headerProps={{
        title: name,
        showHomeButton: true
      }}
      enableStrictMode={assignmentData?.assignment?.settings?.enableStrictMode}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div ref={testHeaderRef} className="fixed left-0 right-0 top-[62px] z-40 bg-white">
          <TestHeader
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={totalQuestions}
            questionLabel={questionLabel}
            score={currentQuestion?.type !== 'fib' ? currentQuestion?.score : undefined}
            rcContext={rcContext}
            headerAddon={statusBadge}
          />
        </div>

        <FloatingNavigationButton onOpenNavigation={() => setIsNavigationOpen(true)} />

        <div
          ref={scrollableRef}
          style={{ paddingTop: scrollPaddingTop }}
          className="assignment-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain pb-[calc(13.5rem+env(safe-area-inset-bottom,0px))] md:pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))]"
        >
          <ReviewQuestionAnswer
            question={currentQuestion}
            questionResponse={currentQuestionResponse}
            isLoading={isFetching}
            answerStatus={answerStatus}
            answerScoreData={currentAnswerScoreData}
            rcContext={rcContext}
            assignmentData={assignmentData}
          />
        </div>

        <div className="fixed bottom-0 left-0 right-0 z-40">
          <ReviewBottomNavigation
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={totalQuestions}
            questionLabel={questionLabel}
            onPrevious={onPrevious}
            onNext={onNext}
            onGoHome={goHome}
            onOpenNavigation={() => setIsNavigationOpen(true)}
            answerStatuses={drawerData}
            rcContext={rcContext}
            isFirstQuestion={isFirstQuestion}
            isLastQuestion={isLastQuestion}
          />
        </div>

        <AssignmentReviewDrawer
          isOpen={isNavigationOpen}
          onClose={() => setIsNavigationOpen(false)}
          answerStatuses={drawerData}
          assignmentQuestionSections={assignmentData?.assignment?.sections || []}
          assignmentQuestions={assignmentData?.assignment?.questions || []}
        />
      </div>
    </LoggedInUserWrapper>
  );
};

export default AssignmentReview;
